import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './RechargeConfirmPage.module.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useSystemLogin } from '../contexts/SystemLoginContext';
import { fetchWithAuth } from '../utils/api';
import config from '../config';
import wechatIcon from '../assets/wechat-icon.png';
import alipayIcon from '../assets/alipay-icon.webp';
import creditCardIcon from '../assets/visamast-icon.png';

interface Position {
  id: string;
  brandId: string | null;
  isTop: number;
  shopId: string;
  image: string;
  radius: number;
  name1: string;
  name2: string;
  name3: string;
  phone: string | null;
  address: string;
  address1: string;
  address2: string;
  address3: string;
  lon: number;
  lat: number;
  memo: string | null;
  cityId: string;
  cityName: string;
  distance: number | null;
  currencySign: string | null;
  positionType: string | null;
  isReplenishmentReward: boolean | null;
  replenishmentRewardType: string | null;
  replenishmentRewardAmount: number | null;
  positionRepertoryVO: any | null;
}

interface PositionResponse {
  position: Position;
  currency: string;
}

interface OrderGoods {
  id: string;
  orderId: string;
  shopId: string;
  positionId: string;
  takePositionId: string | null;
  instanceId: string | null;
  instanceHardwareId: string | null;
  goodsId: string;
  goodsVersion: number;
  goodsSn: string;
  goodsName: string;
  price: number;
  unit: number;
  couponAmount: number;
  orderAmount: number;
  status: number;
  createTime: string;
  updateTime: string | null;
  skuId: number;
  skuNums: number;
  orderGoodsType: number;
  unitId: string | null;
}

interface Order {
  id: string;
  orderType: number;
  shopId: string;
  brandId: string | null;
  userId: string;
  positionId: string;
  orderStatus: number;
  goodsNum: number;
  goodsAmount: number;
  couponAmount: number;
  orderAmount: number;
  actualAmount: number;
  refundAmount: number;
  currencySign: string;
  currency: string;
  payOrderId: string | null;
  payType: number;
  payTime: string | null;
  deliveryTime: string | null;
  deadlineMills: number;
  createTime: string;
  updateTime: string;
  orderAmountStr: string;
  orderAmountDisplayStr: string;
}

interface OrderResponse {
  order: Order;
  orderGoodsList: OrderGoods[];
  orderAmountStr: string;
  orderAmountDisplayStr: string;
}

// 支付方式枚举
enum PaymentType {
  WECHAT_MINI_PROGRAM = '20',
  ALIPAY_H5 = '49',
  WECHAT_QR = '56',
  ALIPAY_QR = '57',
  CREDIT_CARD_NEW = '63'
}

interface PaymentMethod {
  id: PaymentType;
  name: string;
  icon: string;
}

// 支付响应接口
interface PaymentResponse {
  type: number;
  value: string;
  deadlineMills: number;
  thirdOrderNo: string;
  pkSecret: string;
}

const RechargeConfirmPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const systemLogin = useSystemLogin();
  
  // 使用 ref 来防止重复调用
  const initializationAttempted = useRef(false);
  const isFetching = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [currency, setCurrency] = useState<string>('USD');
  const [code, setCode] = useState<string>('');
  const [positionData, setPositionData] = useState<PositionResponse | null>(null);
  const [orderData, setOrderData] = useState<OrderResponse | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<PaymentType | ''>('');
  const [paymentOrderId, setPaymentOrderId] = useState<string | null>(null);
  const checkStatusIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const getPaymentMethodName = (type: PaymentType): string => {
    const methodMap: Record<PaymentType, string> = {
      [PaymentType.WECHAT_MINI_PROGRAM]: '微信小程序',
      [PaymentType.ALIPAY_H5]: '支付宝H5',
      [PaymentType.WECHAT_QR]: '微信扫码',
      [PaymentType.ALIPAY_QR]: '支付宝扫码',
      [PaymentType.CREDIT_CARD_NEW]: '信用卡支付'
    };

    return methodMap[type] || '未知支付方式';
  };

  const getPaymentMethodIcon = (type: PaymentType): string => {
    switch (type) {
      case PaymentType.WECHAT_MINI_PROGRAM:
      case PaymentType.WECHAT_QR:
        return wechatIcon;
      case PaymentType.ALIPAY_H5:
      case PaymentType.ALIPAY_QR:
        return alipayIcon;
      case PaymentType.CREDIT_CARD_NEW:
        return creditCardIcon;
      default:
        return wechatIcon;
    }
  };

  const fetchPaymentTypes = useCallback(async (positionId: string, orderToken: string) => {
    try {
      const cleanOrderToken = orderToken;    
      console.log('调用支付方式接口，positionId:', positionId, 'orderToken:', cleanOrderToken);
      
      const data = await fetchWithAuth(
        `${config.API_BASE_URL}/app-api/aggregatePay/aggregatePayType?positionId=${positionId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json, text/plain, */*',
            'Accept-Language': 'en,zh-CN;q=0.9,zh;q=0.8',
            'Origin': config.APP_BASE_URL,
            'Referer': `${config.APP_BASE_URL}/`,
            'appIntVersion': '11',
            'appversion': '0.7.0',
            'cityId': '1534422005300989952',
            'language': 'CN',
            'languageType': 'CN',
            'lat': '1.323633',
            'lon': '103.800345',
            'orderToken': cleanOrderToken, // 使用大写版本
            'os': 'ios',
          }
        },
        navigate
      );

      console.log('支付方式接口返回数据:', JSON.stringify(data));

      if (data.code === 1 && Array.isArray(data.data)) {
        const validPaymentTypes = [
          PaymentType.WECHAT_MINI_PROGRAM,
          PaymentType.ALIPAY_H5,
          PaymentType.WECHAT_QR,
          PaymentType.ALIPAY_QR,
          PaymentType.CREDIT_CARD_NEW
        ];

        const filteredMethods = data.data
          .filter((type: number | string) => validPaymentTypes.includes(type.toString() as PaymentType))
          .map((type: number | string) => ({
            id: type.toString() as PaymentType,
            name: getPaymentMethodName(type.toString() as PaymentType),
            icon: getPaymentMethodIcon(type.toString() as PaymentType)
          }));

        console.log('过滤后的支付方式:', filteredMethods);

        setPaymentMethods(filteredMethods);
        
        // 如果有可用的支付方式，设置第一个为默认选中
        if (filteredMethods.length > 0) {
          setSelectedMethod(filteredMethods[0].id);
        }
      } else if (data.code === 404 || (data.msg && (data.msg.includes('找不到') || data.msg.includes('not found')))) {
        // 添加对"资源找不到"错误的特殊处理
        console.error('支付方式接口资源找不到:', data.msg || 'Unknown 404 error');
        setError('支付方式不可用');
      } else {
        console.error('支付方式接口未成功:', data.code, data.msg);
        setError(data.msg || '获取支付方式失败');
      }
    } catch (error) {
      console.error('Error fetching payment types:', error);
      setError('获取支付方式失败');
    }
  }, [navigate]);

  const fetchOrder = useCallback(async (orderToken: string) => {
    try {
      const cleanOrderToken = orderToken;      
      console.log('开始获取订单信息, orderToken:', cleanOrderToken);
      
      const data = await fetchWithAuth(
        `${config.API_BASE_URL}/app-api/aggregatePay/getOrder`, 
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json, text/plain, */*',
            'Accept-Language': 'en,zh-CN;q=0.9,zh;q=0.8',
            'Origin': config.APP_BASE_URL,
            'Referer': `${config.APP_BASE_URL}/`,
            'appIntVersion': '11',
            'appversion': '0.7.0',
            'cityId': '1534422005300989952',
            'language': 'CN',
            'languageType': 'CN',
            'lat': '1.323633',
            'lon': '103.800345',
            'orderToken': cleanOrderToken, // 使用大写版本
            'os': 'ios',
          }
        },
        navigate
      );

      // 记录返回的数据用于调试
      console.log('Order API 返回数据:', JSON.stringify(data));

      if (data.code === 1 && data.data) {
        setOrderData(data.data);
        setAmount(data.data.orderAmountStr);
      } else if (data.code === 404 || (data.msg && (data.msg.includes('找不到') || data.msg.includes('not found')))) {
        // 添加对"资源找不到"错误的特殊处理
        console.error('订单API 资源找不到:', data.msg || 'Unknown 404 error');
        setError('订单信息不存在');
      } else {
        console.error('订单API 未成功:', data.code, data.msg);
        setError(data.msg || '获取订单失败');
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      setError('获取订单失败');
    }
  }, [navigate]);

  const fetchPosition = useCallback(async (orderToken: string) => {
    if (loading) {
      return;
    }

    if (isFetching.current) {
      console.log('正在获取数据中，跳过重复请求');
      return;
    }
    isFetching.current = true;
    setLoading(true);
    setError(null);
    try {
      console.log('开始获取商家信息, orderToken:', orderToken);
      
      const cleanOrderToken = orderToken;
      console.log('清理后的orderToken值:', cleanOrderToken);
      
      const data = await fetchWithAuth(
        `${config.API_BASE_URL}/app-api/aggregatePay/getPosition`, 
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json, text/plain, */*',
            'Accept-Language': 'en,zh-CN;q=0.9,zh;q=0.8',
            'Origin': config.APP_BASE_URL,
            'Referer': `${config.APP_BASE_URL}/`,
            'appIntVersion': '11',
            'appversion': '0.7.0',
            'cityId': '1534422005300989952',
            'language': 'CN',
            'languageType': 'CN',
            'lat': '1.323633',
            'lon': '103.800345',
            'orderToken': cleanOrderToken, // 使用大写版本
            'os': 'ios',
          }
        },
        navigate
      );

      // 检查返回的数据
      console.log('Position API 返回数据:', JSON.stringify(data));

      if (data.code === 1 && data.data) {
        setPositionData(data.data);
        // 先获取支付方式列表
        await fetchPaymentTypes(data.data.position.id, cleanOrderToken);
        // 然后获取订单信息
        await fetchOrder(cleanOrderToken);
      } else if (data.code === 404 || (data.msg && (data.msg.includes('找不到') || data.msg.includes('not found')))) {
        console.error('Position API 资源找不到:', data.msg || 'Unknown 404 error');
        setError('商家信息不存在');
      } else {
        console.error('Position API 未成功:', data.code, data.msg);
        setError(data.msg || '获取商家信息失败');
      }
    } catch (error) {
      console.error('Error fetching position:', error);
      setError('获取商家信息失败');
    } finally {
      isFetching.current = false;
      setLoading(false);
    }
  }, [fetchOrder, fetchPaymentTypes, loading, navigate]);

  useEffect(() => {
    if (systemLogin.isLoggedIn && !initializationAttempted.current) {
      initializationAttempted.current = true;
      // 从 URL 参数中获取充值信息
      const params = new URLSearchParams(location.search);
      const codeParam = params.get('code');
      const amountParam = params.get('amount');
      const currencyParam = params.get('currency');

      console.log('URL参数:', location.search);
      console.log('Code参数 (原始):', codeParam);    
      console.log('Amount参数:', amountParam);
      console.log('Currency参数:', currencyParam);

      if (codeParam) {
        setCode(codeParam);
        // 验证金额是否满足最低充值要求
        if (amountParam) {
          const numAmount = Number(amountParam);
          if (numAmount < 0) {
            setError(`充值金额不能少于 ¥${(0).toFixed(2)}`);
            return;
          }
        }
        // 获取 Position 信息，然后获取 Order 信息
        fetchPosition(codeParam);
      } else {
        console.error('缺少code参数');
        setError('缺少必要参数');
      }

      if (amountParam) {
        setAmount(amountParam);
      }

      if (currencyParam) {
        setCurrency(currencyParam);
      }
    }
  }, [fetchPosition, location.search, systemLogin.isLoggedIn]);

  const handleBack = () => {
    navigate('/recharge');
  };

  // 获取币种符号
  const getCurrencySymbol = (currencyCode: string): string => {
    const currencySymbols: { [key: string]: string } = {
      'CNY': '¥',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'KRW': '₩',
      'HKD': 'HK$',
      'SGD': 'S$',
      'CAD': 'C$',
      'AUD': 'A$',
      'CHF': 'CHF',
      'SEK': 'kr',
      'NOK': 'kr',
      'DKK': 'kr',
      'RUB': '₽',
      'INR': '₹',
      'BRL': 'R$',
      'MXN': '$',
      'ZAR': 'R',
      'TRY': '₺',
      'THB': '฿',
      'MYR': 'RM',
      'IDR': 'Rp',
      'PHP': '₱',
      'VND': '₫'
    };
    
    return currencySymbols[currencyCode] || currencyCode;
  };

  const handlePaymentMethodSelect = (methodId: PaymentType) => {
    setSelectedMethod(methodId);
  };

  const checkPaymentStatus = async (orderId: string) => {
    try {
      const data = await fetchWithAuth(
        `${config.API_BASE_URL}/app-api/aggregatePay/queryPayStatus?orderId=${orderId}`, 
        {
          method: 'GET',
          headers: {
            'orderToken': code
          }
        },
        navigate
      );

      console.log('支付状态查询返回:', data);

      if (data.code === 1 && data.data !== undefined) {
        const status = data.data;
        
        // 根据状态处理
        switch (status) {
          case 0:
          case 3:
            // 支付进行中，继续查询
            console.log('支付进行中，继续查询...');
            break;
          case 1:
            // 支付失败，清除定时器并跳转
            if (checkStatusIntervalRef.current) {
              clearInterval(checkStatusIntervalRef.current);
              checkStatusIntervalRef.current = null;
            }
            navigate(`/payment-failed?orderId=${orderId}`);
            break;
          case 2:
            // 支付超时，清除定时器并跳转
            if (checkStatusIntervalRef.current) {
              clearInterval(checkStatusIntervalRef.current);
              checkStatusIntervalRef.current = null;
            }
            navigate(`/payment-timeout?orderId=${orderId}`);
            break;
          case 4:
            // 支付成功，清除定时器并跳转
            if (checkStatusIntervalRef.current) {
              clearInterval(checkStatusIntervalRef.current);
              checkStatusIntervalRef.current = null;
            }
            navigate(`/payment-success?orderId=${orderId}`);
            break;
          default:
            console.error('未知的支付状态:', status);
            // 未知状态继续查询
            break;
        }
      } else {
        console.error('查询支付状态未成功:', data.code, data.msg);
        // 查询失败时不要停止轮询，继续尝试
        console.log('将在下次轮询时重试...');
      }
    } catch (error) {
      console.error('检查支付状态时出错:', error);
      // 发生错误时不要停止轮询，继续尝试
      console.log('将在下次轮询时重试...');
    }
  };
  
  useEffect(() => {
    return () => {
      if (checkStatusIntervalRef.current) {
        clearInterval(checkStatusIntervalRef.current);
      }
    };
  }, []);

  const handleConfirmPayment = async () => {
    if (!code) {
      setError('缺少必要参数');
      return;
    }

    if (!selectedMethod) {
      setError('请选择支付方式');
      return;
    }

    if (!orderData?.order?.id) {
      setError('订单信息缺失');
      return;
    }

    const cleanCode = code;
    
    setLoading(true);
    setError(null);
    // 清除之前的支付状态
    setPaymentOrderId(null);
    
    // 清除之前的定时器
    if (checkStatusIntervalRef.current) {
      clearInterval(checkStatusIntervalRef.current);
      checkStatusIntervalRef.current = null;
    }

    try {
      console.log('确认支付，使用orderId:', orderData.order.id, 'payType:', selectedMethod, 'orderToken:', cleanCode);
      
      const data = await fetchWithAuth(
        `${config.API_BASE_URL}/app-api/aggregatePay/choosePay?orderId=${orderData.order.id}&payType=${selectedMethod}`, 
        {
          method: 'POST',
          headers: {
            'orderToken': cleanCode
          }
        },
        navigate
      );

      console.log('支付确认返回数据:', JSON.stringify(data));
      
      if (data.code === 1 && data.data) {
        const paymentData: PaymentResponse = data.data;
        
        if (paymentData.type === 2) {
          // URL 类型，使用新窗口打开而不是 iframe
          console.log('打开支付 URL:', paymentData.value);
          
          // 保存订单ID用于后续查询
          setPaymentOrderId(orderData.order.id);
          
          // 设置定时器检查支付状态
          checkStatusIntervalRef.current = setInterval(() => {
            checkPaymentStatus(orderData.order.id);
          }, 1000); // 每秒检查一次
          
          // 打开新窗口进行支付
          window.open(paymentData.value, '_blank', 'noopener,noreferrer');
          
          // 显示等待支付的提示信息
          setError('支付窗口已打开，请完成支付');
        } else if (paymentData.type === 1) {
          // 二维码类型，跳转到新页面，添加code参数和支付类型参数
          const paymentMethod = selectedMethod === PaymentType.WECHAT_QR ? 'wechat' : 
                              selectedMethod === PaymentType.ALIPAY_QR ? 'alipay' : 'unknown';
          
          navigate(`/recharge-qr?qrCode=${encodeURIComponent(paymentData.value)}&orderId=${orderData.order.id}&deadlineMills=${paymentData.deadlineMills}&code=${encodeURIComponent(cleanCode)}&paymentMethod=${paymentMethod}&amount=${amount}&currency=${currency}`);
        } else {
          // 其他类型的响应
          console.error('未知的支付类型:', paymentData.type);
          setError('不支持的支付类型');
        }
      } else {
        console.error('支付确认未成功:', data.code, data.msg);
        setError(data.msg || '支付确认失败');
      }
    } catch (error) {
      console.error('Error confirming payment:', error);
      setError('支付确认失败');
    } finally {
      setLoading(false);
    }
  };

  if (!systemLogin.isLoggedIn) {
    return (
      <div className={styles.container}>
        <Header />
        <div className={styles.content}>
          <div className={styles.loginPrompt}>
            <h2>请先登录</h2>
            <p>登录后即可进行充值确认</p>
            <button onClick={() => navigate('/login')} className={styles.loginButton}>
              前往登录
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <header className={styles.header}>
          <button onClick={handleBack} className={styles.backButton}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={styles.backIcon}>
              <path d="M19 12H5M12 19l-7-7 7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            返回
          </button>
          <h1 className={styles.title}>确认充值</h1>
        </header>

        {loading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingSpinner}></div>
            <span>加载中...</span>
          </div>
        )}

        <main className={styles.main}>
          {error ? (
            <div className={styles.errorContainer}>
              <div className={styles.errorMessage}>{error}</div>
              {!paymentOrderId && (
                <button onClick={handleBack} className={styles.retryButton}>
                  返回重试
                </button>
              )}
            </div>
          ) : (
            <>
              <div className={styles.confirmCard}>
                <div className={styles.amountSection}>
                  <div className={styles.amountLabel}>充值金额</div>
                  <div className={styles.amountValue}>
                    <span className={styles.currencySymbol}>{getCurrencySymbol(currency)}</span>
                    <span>{amount}</span>
                  </div>
                </div>

                <div className={styles.paymentMethodSection}>
                  <div className={styles.methodLabel}>选择支付方式</div>
                  <div className={styles.paymentMethodsList}>
                    {paymentMethods.map(method => (
                                        <div
                    key={method.id}
                    className={`${styles.paymentMethodItem} ${selectedMethod === method.id ? styles.selected : ''}`}
                    onClick={() => handlePaymentMethodSelect(method.id)}
                  >
                    <img src={method.icon} alt={method.name} className={styles.paymentMethodIcon} />
                    <span className={styles.paymentMethodName}>{method.name}</span>
                  </div>
                    ))}
                  </div>
                </div>
              </div>

              {paymentOrderId && (
                <div className={styles.paymentStatusCard}>
                  <div className={styles.paymentPendingMessage}>
                    支付窗口已打开，请完成支付
                  </div>
                  <div className={styles.paymentNote}>
                    请在新窗口中完成支付，支付完成后页面会自动跳转
                  </div>
                </div>
              )}

              <button 
                className={styles.confirmButton} 
                onClick={handleConfirmPayment}
                disabled={loading || !code || !selectedMethod}
              >
                {loading ? (
                  <>
                    <span className={styles.loadingSpinner}></span>
                    处理中...
                  </>
                ) : (
                  '确认支付'
                )}
              </button>
            </>
          )}
        </main>
      </div>
      
      <Footer />
    </div>
  );
};

export default RechargeConfirmPage;